import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    message: '',
  });

  // Pré-remplir le message si des paramètres d'URL sont présents
  useEffect(() => {
    const category = searchParams.get('category');
    const search = searchParams.get('search');
    const from = searchParams.get('from');
    
    if (category || search || from) {
      let prefillMessage = 'Bonjour,\n\nJe recherche une pièce spécifique :\n\n';
      if (category) prefillMessage += `Catégorie : ${category}\n`;
      if (search) prefillMessage += `Recherche : ${search}\n`;
      if (from) prefillMessage += `Page visitée : ${from}\n`;
      prefillMessage += '\nMerci de me recontacter.\n';
      
      setFormData(prev => ({ ...prev, message: prefillMessage }));
    }
  }, [searchParams]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, {
        type: 'contact',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        location: `${formData.address}, ${formData.city}`,
        message: formData.message,
      });

      // Track form submission
      trackContactFormSubmit();
      setSubmitted(true);
      toast.success('Message envoyé !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <SEO 
        title="Contact - Atlantique Spa Boutique"
        description="Contactez Atlantique Spa Boutique pour votre projet spa à Nantes et Grand Ouest. Technicien spécialiste, devis gratuit, SAV local. Email: contact@atlantiquespaboutique.com"
        canonical="/contact"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Contact</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero Section - Full Width Image */}
      <FullWidthHero
        variant="compact"
        image={realPhotos.installation1}
        imageAlt={realPhotosAlt.installation1}
        title="Contactez-nous"
        subtitle="Une question, un projet ? Notre technicien est à votre écoute."
        overlayPosition="left"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Cards */}
          <div className="space-y-6">
            <Card className="border-slate-200 card-hover">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:contact@atlantiquespaboutique.com" className="text-blue-600 hover:text-blue-800 break-all">
                      contact@atlantiquespaboutique.com
                    </a>
                    <p className="text-sm text-slate-500 mt-1">Réponse rapide garantie</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 card-hover">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Send className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Formulaire de contact</h3>
                    <Link to="/devis" className="text-orange-600 hover:text-orange-800 font-medium">
                      Demander un devis gratuit →
                    </Link>
                    <p className="text-sm text-slate-500 mt-1">Réponse sous 24h</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 card-hover">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:contact@atlantiquespaboutique.com" className="text-blue-600 hover:text-blue-800 break-all">
                      contact@atlantiquespaboutique.com
                    </a>
                    <p className="text-sm text-slate-500 mt-1">Réponse sous 24h</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Zone d'intervention</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>Loire-Atlantique (44)</li>
                      <li>Vendée (85)</li>
                      <li>Morbihan (56)</li>
                      <li>Maine-et-Loire (49)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200">
              <CardContent className="p-6 md:p-8">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Message envoyé !</h3>
                    <p className="text-slate-600">Nous vous recontacterons dans les plus brefs délais.</p>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-semibold text-slate-900 mb-6">Envoyez-nous un message</h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">Nom *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            required
                            placeholder="Votre nom"
                            data-testid="contact-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            placeholder="06 12 34 56 78"
                            data-testid="contact-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                          placeholder="votre@email.com"
                          data-testid="contact-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="address">Adresse</Label>
                        <Input
                          id="address"
                          value={formData.address}
                          onChange={(e) => handleChange('address', e.target.value)}
                          placeholder="12 rue des Jardins"
                          data-testid="contact-address"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city">Code postal et ville</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          placeholder="44000 Nantes"
                          data-testid="contact-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          required
                          rows={6}
                          placeholder="Décrivez votre demande..."
                          data-testid="contact-message"
                        />
                      </div>
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-slate-900 hover:bg-slate-800"
                        disabled={loading}
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Envoi...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Envoyer le message
                          </span>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* SAV Link */}
        <div className="mt-12 text-center">
          <Card className="border-slate-200 bg-slate-100 inline-block">
            <CardContent className="p-6">
              <p className="text-slate-600 mb-2">Besoin d'une réparation ou d'un entretien ?</p>
              <a 
                href="https://www.atlantiquespaservice.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Visitez notre site SAV → atlantiquespaservice.com
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
